import { Injectable } from '@angular/core';
import * as LaunchDarklyClient from 'launchdarkly-js-client-sdk';
import { FeatureFlags } from './featureFlags.enum';
import { adminConfig } from 'src/environments/environment';

export type FeatureFlagType = {
  [key in keyof typeof FeatureFlags]: string;
};

@Injectable({
  providedIn: 'root',
})
export class LaunchDarklyService {
  private launchDarklyClient!: LaunchDarklyClient.LDClient;
  private user = {
    key: 'admin-ui',
    anonymous: true,
  };

  private clientReady!: Promise<void>;
  private _featureFlags: Record<string, any> = {};

  constructor() {}

  public async initialize(): Promise<void> {
    try {
      const key = adminConfig.launchDarklyAccessKey;

      if (!key) {
        throw new Error('LaunchDarkly access key is missing');
      }

      this.launchDarklyClient = await LaunchDarklyClient.initialize(key, this.user);

      this.clientReady = new Promise((resolve) => {
        this.launchDarklyClient.on('ready', () => {
          resolve();
        });
      });

      await this.initializeFeatureFlags();
    } catch (error) {
      console.error('Failed to initialize LaunchDarkly client:', error);
    }

    return Promise.resolve();
  }

  private async initializeFeatureFlags(): Promise<void> {
    await this.isReady();

    const flagKeys = this.getEnumValues(FeatureFlags as FeatureFlagType);

    for (const flagKey of flagKeys) {
      this.featureFlags[flagKey] = await this.getFeatureFlag(flagKey, false);

      this.onFlagChange(flagKey, (value) => {
        this.featureFlags[flagKey] = value;
      });
    }
  }

  private getEnumValues(enumObj: FeatureFlagType): string[] {
    return Object.values(enumObj);
  }

  public async getFeatureFlag(flagKey: string, defaultValue: any): Promise<any> {
    return this.launchDarklyClient.variation(flagKey, defaultValue);
  }

  public onFlagChange(flagKey: string, callback: (value: boolean) => void): void {
    this.launchDarklyClient.on(`change:${flagKey}`, callback);
  }

  public isReady(): Promise<void> {
    return this.clientReady;
  }

  public get PlannedHandoffExceptionsEnabled(): boolean {
    return this.featureFlags[FeatureFlags.PlannedHandoffExceptionsEnabled] ?? false;
  }

  public get PlannedDeliveryExceptions(): boolean {
    return this.featureFlags[FeatureFlags.PlannedDeliveryExceptions] ?? false;
  }

  public get featureFlags(): Record<string, any> {
    return this._featureFlags;
  }
}
